import type { ReactNode } from 'react';
import clsx from 'clsx';
import React from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';
import { DeviceSize, useDeviceSize } from 'wrappers/DeviceSizeProvider';

import { TextButton } from '@fragment/ui/src/components/Button/TextButton/TextButton';
import { Icon } from '@fragment/ui/src/components/Icon/BaseIcon/Icon';

const kSidebarWidth = 'w-f18 min-w-f18 max-w-f18';
export type SideBarStageName =
  | 'notice'
  | 'business'
  | 'incorporation'
  | 'businessCDD'
  | 'people'
  | 'accounts'
  | 'submit';

const mapStageToLabel = new Map<SideBarStageName, string>([
  ['notice', 'Data Collection Notice'],
  ['business', 'Business Information'],
  ['incorporation', 'Incorporation'],
  ['businessCDD', 'Business Details'],
  ['people', 'People'],
  ['accounts', 'Connect Your Accounts'],
  ['submit', 'Submit'],
]);

type SideBarProps = {
  currentStage: SideBarStageName;
  completedStages: SideBarStageName[];
  onClickStage: (stage: SideBarStageName) => void;
  workspaceId: string;
};

const SideBar = ({
  currentStage,
  completedStages,
  onClickStage,
  workspaceId,
}: SideBarProps) => {
  const [, setLocation] = useLocation();
  const stages: { label: string; value: SideBarStageName }[] = [
    {
      label: 'Notice',
      value: 'notice',
    },
    {
      label: 'Business',
      value: 'business',
    },
    {
      label: 'Incorporation',
      value: 'incorporation',
    },
    {
      label: 'Details',
      value: 'businessCDD',
    },
    {
      label: 'People',
      value: 'people',
    },
    {
      label: 'Accounts',
      value: 'accounts',
    },
    {
      label: 'Submit',
      value: 'submit',
    },
  ];
  return (
    <div
      className={clsx(
        'flex flex-col h-full border-r-[0.5px] border-main-300 ',
        kSidebarWidth
      )}
    >
      <div className="p-f2 border-b-[0.5px] border-main-300">
        <TextButton
          icon={<Icon type="exitLeftA" size="md" />}
          hoverIcon={<Icon type="exitLeftB" size="md" />}
          onClick={() => {
            setLocation(
              makePath(['w', workspaceId, 'payments', 'payment-settings'], {
                absolute: true,
              })
            );
          }}
        >
          Exit
        </TextButton>
      </div>
      <span className="h-f2" />
      {stages.map((item) => {
        const isDisabled =
          !completedStages.includes(item.value) && currentStage !== item.value;

        const showIcon = completedStages.includes(item.value);
        return (
          <button
            type="button"
            key={item.value}
            onClick={(e) => {
              e.preventDefault();
              if (currentStage === item.value) {
                return;
              }
              onClickStage(item.value);
            }}
            className={clsx(
              'flex w-full items-center h-f2 pl-f1 text-left',
              currentStage === item.value
                ? 'bg-main-200 text-main'
                : 'bg-transparent text-main-500 hover:text-main',
              'disabled:cursor-not-allowed disabled:hover:text-main-500'
            )}
            disabled={isDisabled}
          >
            {showIcon ? (
              <Icon type="check" size="md" />
            ) : (
              <span className="w-[1ch]" />
            )}
            {item.label}
          </button>
        );
      })}
    </div>
  );
};

type LayoutProps = {
  children: ReactNode;
  currentStage: SideBarStageName;
  completedStages: SideBarStageName[];
  onClickStage: (stage: SideBarStageName) => void;
  workspaceId: string;
};

export const OnboardingLayout = ({
  children,
  currentStage,
  completedStages,
  onClickStage,
  workspaceId,
}: LayoutProps) => {
  const deviceSize = useDeviceSize();
  const isMobile =
    deviceSize === DeviceSize.Phone || deviceSize === DeviceSize.Tablet;

  return (
    <div className="flex flex-row h-[100dvh] w-[100dvw] bg-canvas">
      {!isMobile && (
        <SideBar
          currentStage={currentStage}
          completedStages={completedStages}
          onClickStage={onClickStage}
          workspaceId={workspaceId}
        />
      )}
      <div className="flex flex-col w-full h-full">
        <div className="flex w-full h-fit p-f2 border-b-[0.5px] border-main-300">
          <span className="text-md text-main-500">
            {mapStageToLabel.get(currentStage)}
          </span>
        </div>
        <div className="flex flex-col flex-1 min-h-0 w-full laptop:w-f56">
          <div className="flex flex-col p-f2 flex-1 min-h-0 overflow-y-scroll gap-y-f2">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};
